
import CarouselPageButton from 'Src/js/components/carousels/CarouselPageButton.vue';
import Icon from 'Src/js/components/Icon.vue';
import Price from 'JS/components/price/Price.vue';
import SkeletonLoader from 'Src/js/components/SkeletonLoader.vue';

import CarouselTrackingService from 'JS/services/CarouselTrackingService';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { createCarouselClickData, createCarouselClickGtmData, createCarouselViewGtmData, handleTrackingLinkClick } from '../../utilities/carousel-tracking';
import { createListSlider, IListSlider } from 'JS/list-slider';
import { IHawkRecommendationsCarouselData, IProductCarouselProduct, isHawkCarousel, ProductCarousel } from 'Src/js/types/Carousel';
import { lazyLoadImages } from 'JS/global/images';
import { onBreakpointChange } from 'JS/utilities/viewport';

@Component({
	name: 'SimilarProductsCarousel',
	components: {
		CarouselPageButton,
		Price,
		Icon,
		SkeletonLoader,
	},
})
export default class SimilarProductsCarousel extends Vue {

	$refs!: {
		btnPrev: Vue & { $el: HTMLButtonElement };
		btnNext: Vue & { $el: HTMLButtonElement };
		viewport: HTMLDivElement;
	}

	/* Props
	============================================*/

	@Prop({ type: Object as () => ProductCarousel })
	carousel: ProductCarousel | null;

	@Prop({ type: Boolean })
	loading: boolean;

	@Prop({ type: Object as () => Partial<IHawkRecommendationsCarouselData>, default: () => ({}) })
	customClickData: IHawkRecommendationsCarouselData;

	/* Data
	============================================*/

	slider: IListSlider | null;
	impressionTracked = false;

	/* Computed
	============================================*/

	get title() {
		if (this.loading || !this.carousel?.title) {
			return null;
		}

		return this.carousel.title;
	}

	get products(): IProductCarouselProduct[] {
		if (this.loading || !this.carousel) {
			return [];
		}

		/**
		 * Replace the product's price alt text with "View Details"
		 *
		 * This is done to maintain consistent vertical alignment across the slider
		 * The actual price alt text may vary in length and wrap; this prevents that
		 *
		 * Remove "Today's Price" -- too much visual noise on every product
		 */
		return this.carousel?.products?.map(product => {
			// patch the alt text and isBelowMap prop; the price component does something
			// special with below MAP prices
			const p = { ...product, price: { ...product.price } };

			if (p.price.priceAltText) {
				p.price.isBelowMAP = false;
				p.price.priceAltText = 'View Details';
			}

			if (p.price.pricePromoText === "Today's Price") {
				p.price.pricePromoText = '';
			}

			return p;
		});
	}

	/* Methods
	============================================*/

	initializeSlider() {
		if (this.loading || !this.carousel?.products) return;
		this.$nextTick(() => {
			if (!this.slider) {
				this.slider = createListSlider(this.$refs.viewport);
				this.slider.setControls(this.$refs.btnPrev.$el, this.$refs.btnNext.$el);
			}
			lazyLoadImages(this.$refs.viewport);
		});
		this.trackInitialView();
	}

	trackClick(e: MouseEvent, product: IProductCarouselProduct) {
		handleTrackingLinkClick(e, () => {
			const svc = new CarouselTrackingService();
			const customClickData = isHawkCarousel(this.carousel)
				? {
					hawkRequestId: this.carousel.requestId,
					hawkWidgetType: this.carousel.widgetType,
				}
				: this.customClickData;
			const clickData = createCarouselClickData(product, this.carousel, customClickData);
			const gtmData = createCarouselClickGtmData(product, this.carousel);
			svc.trackClick(clickData, gtmData);
		});
	}

	async trackInitialView() {
		if (this.impressionTracked || this.loading || !this.carousel) return;
		this.impressionTracked = true;
		const svc = new CarouselTrackingService();
		const gtmData = createCarouselViewGtmData(this.carousel);
		await svc.trackViews([gtmData]);
	}

	/* Lifecycle
	============================================*/

	created() {
		this.slider = null;
	}

	mounted() {
		this.initializeSlider();

		const breakpointListener = onBreakpointChange(() => this.slider?.reset());
		this.$on('hook:beforeDestroy', () => breakpointListener.remove());
	}

	@Watch('loading')
	loadingChanged(isLoading: boolean) {
		if (isLoading) return;
		this.initializeSlider();
	}

	@Watch('products')
	onProductsChanged(newProducts: IProductCarouselProduct[]) {
		if (!newProducts || !Array.isArray(newProducts)) return;
		this.initializeSlider();
	}
}
